import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { AppService } from '../_shared/services/app.service';
import { PermissionService } from '../_shared/services/permission.service';
import { ProjectService } from '../_shared/services/project.service';
import { QuoteService } from '../_shared/services/quote.service';
import { SelectedCustomerService } from '../_shared/services/selected-customer.service';
import { SnackBarService } from '../_shared/services/snack-bar.service';
import { ValidateValueStateMatcher } from '../_shared/validation/ValidateValueStateMatcher';
import { ProductListService } from '../_shared/components/product-list/product-list.service';

@Component({
    selector: 'app-configuration',
    templateUrl: './configuration.component.html',
    styleUrls: ['./configuration.component.scss'],
    standalone: false
})
export class ConfigurationComponent implements OnInit, OnDestroy {
    @ViewChild('questionaire')
    public questionaireElement: ElementRef;

    @ViewChild('offerTitleInput')
    public offerTitleElement: ElementRef;

    @ViewChild('contentContainer')
    public wrapperDiv: ElementRef;
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    private originalOfferTitle: string;
    private defaultOfferTitle: string;

    public loaderTextKey = 'CONFIGURATION.LOADER.LOADING_WIZARD';
    public isOfferTitleDisabled = true;
    public offerTitle: string;
    public projectIdControl: FormControl<string>;
    public quoteId: string;
    public quickRef = '...';

    public isQuestFormReady = false;
    public isQuestFormComplete = false;
    public errorOnEmptyMatcher = new ValidateValueStateMatcher((value) => value !== '');

    public isCustomer =
        this.permissionService.userInfo$.value && this.permissionService.userInfo$.value.role === 'customer';

    constructor(
        private activatedRoute: ActivatedRoute,
        private appService: AppService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private quoteService: QuoteService,
        public permissionService: PermissionService,
        private selectedCustomerService: SelectedCustomerService,
        private projectService: ProjectService,
        private productListService: ProductListService
    ) {}

    public submit() {
        (<any>this.questionaireElement).submit();
    }

    public onQuestFormReady() {
        this.isQuestFormReady = true;
    }

    public onQuestFormComplete(isComplete: boolean) {
        this.isQuestFormComplete = isComplete;
        const selectedPartner = this.selectedCustomerService.customerForPartnerMode$.value;
        if (selectedPartner && !this.selectedCustomerService.selectedCustomer$.value) {
            // preset selected partner if partner mode was active
            this.selectedCustomerService.selectedCustomer$.next(selectedPartner);
        }
    }

    public onQuestSubmit() {
        if (!this.isCustomer) {
            this.projectIdControl.markAsTouched();
        }
    }

    public ngOnInit() {
        this.productListService.clearEdits();
        this.defaultOfferTitle = this.translateService.instant('COMMON.NEW_OFFER');
        this.projectIdControl = new FormControl<string>('', {
            validators: this.isCustomer ? [] : [Validators.required],
            asyncValidators: this.isCustomer ? [] : [this.validateProjectNumber.bind(this)],
            updateOn: 'blur',
        });

        this.projectIdControl.statusChanges
            .pipe(
                filter((status) => status === 'VALID' && !!this.projectIdControl.value),
                map(() => this.projectIdControl.value.padStart(10, '0')),
                switchMap((projectId) => {
                    this.onOfferTitleBlur();
                    return this.quoteService.updateProjectId(projectId);
                })
            )
            .subscribe();
        this.quoteService
            .getCurrentQuoteObservable()
            .pipe(filter((q) => !!q))
            .subscribe((quote) => {
                this.offerTitle = quote.customerPurchaseNumber;
                this.originalOfferTitle = this.offerTitle;
                this.quickRef = quote.quickRef;
                const projectNumber = quote.projectId;
                if (projectNumber && !this.projectIdControl.value) {
                    this.projectIdControl.setValue(projectNumber);
                    this.projectIdControl.markAsTouched();
                }
            });

        this.assignPropertiesFromActivatedRoute();
        this.quoteService.quoteErrors$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            this.showSnackBar('CONFIGURATION.SNACK_BAR.SAVE_QUOTE.ERROR', true);
        });
    }

    public onOfferTitleClick() {
        this.isOfferTitleDisabled = false;
    }

    public onOfferTitleBlur() {
        this.originalOfferTitle = this.originalOfferTitle || '';
        this.offerTitle = this.offerTitle || '';

        this.isOfferTitleDisabled = true;

        if (this.originalOfferTitle !== this.offerTitle.trim()) {
            this.updateQuoteTitle(this.offerTitle);
        }
    }

    private assignPropertiesFromActivatedRoute(): void {
        const { id, salesforceId } = this.activatedRoute.snapshot.queryParams;

        this.quoteId = id;
        if (salesforceId) {
            this.appService.salesforceId$.next(salesforceId);
        }
    }

    private validateProjectNumber(control: AbstractControl): Observable<ValidationErrors> {
        return this.projectService.getProject(control.value).pipe(
            map((project) => {
                const invalid = project.error || project.salesOrg !== this.appService.getSalesOrg();
                const primaryPartner = project.projectPartners?.find((p) => p.isPrimaryPartner === true);
                if (primaryPartner) {
                    this.selectedCustomerService.selectedCustomer$.next({ number: primaryPartner.sapId });
                }
                const isTitleSet = !!this.offerTitle && this.offerTitle !== this.defaultOfferTitle;
                // If there is no title yet and the project number is valid, set the title equal to the project description.
                if (!isTitleSet && !invalid) {
                    this.offerTitle = project.description1;
                }
                return invalid ? { invalid: true } : null;
            })
        );
    }

    private updateQuoteTitle(newTittle: string) {
        this.quoteService.updateQuoteTitle(newTittle);
        this.originalOfferTitle = this.offerTitle;
        this.showSnackBar('CONFIGURATION.SNACK_BAR.UPDATE_OFFER_TITLE.SUCCESS', false);
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private showSnackBar(translationKey: string, isFailure: boolean): void {
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
            isFailure,
        });
    }
}
